import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs/operators';

import { SideNavComponent } from '../Layout/side-nav/side-nav.component';
import { AuthService } from './modules/auth/auth.service';
import { AppDataService } from './core/services/app-data.service';
import { watchMobileTableLabels } from './core/utils/table-mobile.util';

const WARN_BEFORE_MS = 5 * 60 * 1000;
const CHECK_EVERY_MS = 30 * 1000;

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, SideNavComponent],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
})
export class AppComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private readonly auth = inject(AuthService);
  private readonly appData = inject(AppDataService);
  private stopTableWatch: (() => void) | null = null;
  private sessionTimer: ReturnType<typeof setInterval> | null = null;

  title = 'ims-frontend';
  sideNavOpen = false;
  showShell = false;

  protected readonly showSessionWarning = signal(false);
  protected readonly sessionMinutesLeft = signal(5);
  protected readonly sessionExtending = signal(false);

  ngOnInit() {
    this.updateShellVisibility(this.router.url);
    this.stopTableWatch = watchMobileTableLabels();
    this.startSessionWatch();

    if (this.auth.isAuthenticated()) {
      this.appData.refreshAll();
    }

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        const navigation = event as NavigationEnd;
        this.updateShellVisibility(navigation.urlAfterRedirects);
        this.sideNavOpen = false;
        this.checkSessionExpiry();
      });
  }

  ngOnDestroy() {
    this.stopTableWatch?.();
    if (this.sessionTimer) {
      clearInterval(this.sessionTimer);
      this.sessionTimer = null;
    }
  }

  toggleSideNav() {
    this.sideNavOpen = !this.sideNavOpen;
  }

  closeSideNav() {
    this.sideNavOpen = false;
  }

  extendSession(): void {
    if (this.sessionExtending()) {
      return;
    }
    this.sessionExtending.set(true);
    this.auth.refreshSession().subscribe((result) => {
      this.sessionExtending.set(false);
      if (result.success) {
        this.showSessionWarning.set(false);
        this.checkSessionExpiry();
      }
    });
  }

  dismissSessionWarning(): void {
    this.showSessionWarning.set(false);
  }

  private startSessionWatch(): void {
    this.checkSessionExpiry();
    this.sessionTimer = setInterval(() => this.checkSessionExpiry(), CHECK_EVERY_MS);
  }

  private checkSessionExpiry(): void {
    if (!this.showShell || !this.auth.isAuthenticated()) {
      this.showSessionWarning.set(false);
      return;
    }

    const remaining = this.auth.msUntilExpiry();
    if (remaining == null) {
      return;
    }

    if (remaining <= 0) {
      this.showSessionWarning.set(false);
      this.auth.clearLocalSession();
      void this.router.navigate(['/login'], {
        queryParams: { reason: 'expired' },
      });
      return;
    }

    if (remaining <= WARN_BEFORE_MS) {
      this.sessionMinutesLeft.set(Math.max(1, Math.ceil(remaining / 60000)));
      this.showSessionWarning.set(true);
    } else {
      this.showSessionWarning.set(false);
    }
  }

  private updateShellVisibility(url: string) {
    const path = url.split('?')[0] || '/';
    const authPaths = ['/', '/login', '/otp', '/forgot-password', '/reset-password'];
    this.showShell = !authPaths.includes(path);
  }
}
